import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Stack,
  Typography,
  Tabs,
  Tab,
  Alert,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Close,
  Image as ImageIcon,
  Palette,
  Settings,
  AttachMoney,
  DeleteOutline,
  AddPhotoAlternate,
} from '@mui/icons-material';
import { useMutation, useQueryClient } from 'react-query';
import client from '../../../../../API';
import { replaceConsoleError } from '../../../../../Utils/toastNotifications';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`settings-tabpanel-${index}`}
    aria-labelledby={`settings-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const VALID_STATUSES = [
  { value: 'ACTIVE', label: 'Active', color: 'success' },
  { value: 'COMPLETED', label: 'Completed', color: 'default' },
];

const SettingsDialog = ({ open, onClose, castingCall, onSuccess }) => {
  const queryClient = useQueryClient();
  const [currentTab, setCurrentTab] = useState(0);
  const [formData, setFormData] = useState({
    brief: '',
    description: '',
    budget: '',
    header_image: '',
    supporting_images: [],
    theme_colors: {
      primary: '#1976d2',
      secondary: '#dc004e',
      accent: '#ff9800',
    },
    auto_generate_contract: true,
    status: 'ACTIVE',
  });
  const [errors, setErrors] = useState({});
  const [newImageUrl, setNewImageUrl] = useState('');

  // Initialize form data when dialog opens
  useEffect(() => {
    if (open && castingCall) {
      setFormData({
        brief: castingCall.brief || '',
        description: castingCall.description || '',
        budget: castingCall.budget ? parseFloat(castingCall.budget).toFixed(2) : '',
        header_image: castingCall.header_image || '',
        supporting_images: castingCall.supporting_images || [],
        theme_colors: castingCall.theme_colors || {
          primary: '#1976d2',
          secondary: '#dc004e',
          accent: '#ff9800',
        },
        auto_generate_contract: castingCall.auto_generate_contract !== false,
        status: castingCall.status || 'ACTIVE',
      });
      setErrors({});
      setCurrentTab(0);
    }
  }, [open, castingCall]);

  const { mutate: updateCastingCall, isLoading } = useMutation(
    (data) => client.castingCalls.update(castingCall.id, data),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['castingCall', castingCall.id]);
        queryClient.invalidateQueries(['castingCallBudget', castingCall.id]);
        if (onSuccess) onSuccess(response);
        onClose();
      },
      onError: (error) => {
        replaceConsoleError('Error updating casting call', error);
        const errorMsg = error?.response?.data?.error || error.message;
        setErrors({ submit: errorMsg });
      },
    }
  );

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null, submit: null }));
  };

  const handleThemeColorChange = (colorKey, value) => {
    setFormData((prev) => ({
      ...prev,
      theme_colors: {
        ...prev.theme_colors,
        [colorKey]: value,
      },
    }));
  };

  const handleAddSupportingImage = () => {
    if (!newImageUrl.trim()) return;
    
    setFormData((prev) => ({
      ...prev,
      supporting_images: [...prev.supporting_images, newImageUrl.trim()],
    }));
    setNewImageUrl('');
  };

  const handleRemoveSupportingImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      supporting_images: prev.supporting_images.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.brief.trim()) {
      newErrors.brief = 'Brief is required';
    } else if (formData.brief.trim().length < 50) {
      newErrors.brief = 'Brief must be at least 50 characters';
    } else if (formData.brief.trim().length > 500) {
      newErrors.brief = 'Brief must be 500 characters or less';
    }

    if (!formData.budget || parseFloat(formData.budget) <= 0) {
      newErrors.budget = 'Budget must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const updateData = {
      brief: formData.brief.trim(),
      description: formData.description.trim() || null,
      budget: parseFloat(formData.budget),
      header_image: formData.header_image.trim() || null,
      supporting_images: formData.supporting_images,
      theme_colors: formData.theme_colors,
      auto_generate_contract: formData.auto_generate_contract,
      status: formData.status,
    };

    updateCastingCall(updateData);
  };

  const hasChanges = () => {
    if (!castingCall) return false;
    
    return (
      formData.brief !== (castingCall.brief || '') ||
      formData.description !== (castingCall.description || '') ||
      parseFloat(formData.budget) !== parseFloat(castingCall.budget || 0) ||
      formData.header_image !== (castingCall.header_image || '') ||
      JSON.stringify(formData.supporting_images) !== JSON.stringify(castingCall.supporting_images || []) ||
      JSON.stringify(formData.theme_colors) !== JSON.stringify(castingCall.theme_colors || {}) ||
      formData.auto_generate_contract !== (castingCall.auto_generate_contract !== false) ||
      formData.status !== castingCall.status
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Casting Call Settings
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <Tabs
        value={currentTab}
        onChange={(e, newValue) => setCurrentTab(newValue)}
        sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}
      >
        <Tab label="Basic Info" />
        <Tab label="Visual Settings" />
        <Tab label="Status & Advanced" />
      </Tabs>

      <DialogContent>
        {errors.submit && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.submit}
          </Alert>
        )}

        {/* Basic Info Tab */}
        <TabPanel value={currentTab} index={0}>
          <Stack spacing={3}>
            <TextField
              label="Brief"
              value={formData.brief}
              onChange={(e) => handleChange('brief', e.target.value)}
              error={!!errors.brief}
              helperText={errors.brief || `${formData.brief.length}/500 characters (min 50)`}
              multiline
              rows={3}
              required
              fullWidth
            />

            <TextField
              label="Description (Optional)"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              multiline
              rows={5}
              fullWidth
              helperText="Extended description of the casting call"
            />


            {castingCall && parseFloat(formData.budget) !== parseFloat(castingCall.budget) && (
              <Alert severity="info">
                <Typography variant="body2">
                  Current budget: ${parseFloat(castingCall.budget).toFixed(2)}
                  {castingCall.deliverables_scope?.payout_assignments && (
                    <> • Allocated: ${Object.values(castingCall.deliverables_scope.payout_assignments).reduce((sum, amt) => sum + parseFloat(amt), 0).toFixed(2)}</>
                  )}
                </Typography>
              </Alert>
            )}
          </Stack>
        </TabPanel>

        {/* Visual Settings Tab */}
        <TabPanel value={currentTab} index={1}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Header Image
              </Typography>
              <TextField
                placeholder="https://example.com/image.jpg"
                value={formData.header_image}
                onChange={(e) => handleChange('header_image', e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ImageIcon />
                    </InputAdornment>
                  ),
                }}
              />
              {formData.header_image && (
                <Box
                  component="img"
                  src={formData.header_image}
                  alt="Header preview"
                  sx={{
                    mt: 2,
                    width: '100%',
                    maxHeight: 200,
                    objectFit: 'cover',
                    borderRadius: 1,
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Supporting Images
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  placeholder="https://example.com/image.jpg"
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  size="small"
                  fullWidth
                />
                <Button
                  variant="outlined"
                  onClick={handleAddSupportingImage}
                  startIcon={<AddPhotoAlternate />}
                  disabled={!newImageUrl.trim()}
                >
                  Add
                </Button>
              </Box>

              <Stack spacing={1}>
                {formData.supporting_images.map((url, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Box
                      component="img"
                      src={url}
                      alt={`Supporting ${index + 1}`}
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                      }}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/60';
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1, fontSize: '0.75rem' }} noWrap>
                      {url}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveSupportingImage(index)}
                      color="error"
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>

              {formData.supporting_images.length === 0 && (
                <Alert severity="info">
                  No supporting images added yet
                </Alert>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Palette fontSize="small" />
                Theme Colors
              </Typography>
              
              <Stack spacing={2}>
                {['primary', 'secondary', 'accent'].map((colorKey) => (
                  <Box key={colorKey} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        bgcolor: formData.theme_colors[colorKey],
                        border: 1,
                        borderColor: 'divider',
                      }}
                    />
                    <TextField
                      label={colorKey.charAt(0).toUpperCase() + colorKey.slice(1)}
                      value={formData.theme_colors[colorKey]}
                      onChange={(e) => handleThemeColorChange(colorKey, e.target.value)}
                      size="small"
                      fullWidth
                      placeholder="#1976d2"
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        </TabPanel>

        {/* Status & Advanced Tab */}
        <TabPanel value={currentTab} index={2}>
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                label="Status"
              >
                {VALID_STATUSES.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={status.label}
                        color={status.color}
                        size="small"
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {formData.status !== castingCall?.status && (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Status Change Warning
                </Typography>
                <Typography variant="caption">
                  {formData.status === 'COMPLETED' && 'Completing this casting call will prevent new applications.'}
                  {formData.status === 'DELETED' && 'Deleting this casting call cannot be undone.'}
                  {formData.status === 'ACTIVE' && castingCall?.status === 'COMPLETED' && 'Cannot reopen a completed casting call.'}
                </Typography>
              </Alert>
            )}

            <Divider />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.auto_generate_contract}
                  onChange={(e) => handleChange('auto_generate_contract', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Auto-Generate Contracts
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Automatically create contracts when selecting applicants
                  </Typography>
                </Box>
              }
            />
          </Stack>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || !hasChanges()}
          startIcon={isLoading && <CircularProgress size={16} />}
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsDialog;